/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    object, string, shape, bool,
} from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import Media from 'react-media';

// Styles
import { main as MainStyle } from '../style/main';

// Material UI
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';

// Helpers
import configENV from '../config';
import EventDispatcher from '../state/reducers/EventDispatcher';
import { getFeatureFlags, getCurrentBrandId, getProfileInfo } from '../state/Selectors';
import { CUSTOM_BREAKPOINTS } from '../style/variables';
import { isEmpty } from '../utils/object';
import { getIsAuthenticatedStatus } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import AccountLeftMenu from '../common/Desktop/AccountLeftMenu';
import { OrderDetailsContextProvider } from './OrderDetails/Context/OrderDetailsContext';

// Components
import AccountHome from './MyAccount/AccountHome';
import AddressBookContainer from './AddressBook/AddressBookContainer';
import RecipientBookContainer from './AddressBook/RecipientBook/RecipientBookContainer';
import BillingAddress from './BillingAddress/BillingAddress';
import TrackOrder from './TrackOrder/TrackOrder';
import AlertMessage from '../../../components/GraphqlComponents/AlertMessage/AlertMessage';
import OrderDetails from './OrderDetails/OrderDetails';
import ProfileAndPassword from './ProfileAndPassword/ProfileAndPassword';
import Passport from './Passport/Passport';
import ChangePassword from './ChangePassword/ChangePassword';
import ChangeEmail from './ChangeEmail/ChangeEmail';
import EmailPreferenceContainer from './EmailPreference/EmailPreferenceContainer';
import RecipientVerify from './RecipientGiftMessage/RecipientVerify';
import PoemPage from './ChatGPT/Poems/PoemPage';
import FloraVerse from './ChatGPT/Poems/FloraVerse';
import UIModal from '../common/UIModal';
import PlayBook from './PlayBook/PlayBook';
import { Helmet } from 'react-helmet';
import CommunicationCenter from '../components/CommunicationCenter/CommunicationCenter';
import RemindersOptOutPage from './Reminders/RemindersOptOutPage';
import EditRecipientContainer from '../components/AddressBook/RecipientBook/Common/EditRecipient/EditRecipientContainer';
import CelebrationsRemindersPage from '../../../components/GraphqlComponents/CelebrationsReminders/CelebrationsRemindersPage';
import mbpUtil from 'mbp-api-util';
import GraphqlRelationship from '../../../components/GraphqlComponents/GraphqlRelationship/GraphqlRelationship';

const { ENV_NAME } = configENV;

const styles = ({ typography, palette }) => {
    if (ENV_NAME === 'DEV') console.log('palette', palette);
    return MainStyle(typography, palette);
};

const Components = {
    AccountHome,
    TrackOrder,
    OrderDetails,
    Passport,
    AddressBookContainer,
    RecipientBookContainer,
    BillingAddress,
    ProfileAndPassword,
    ChangePassword,
    ChangeEmail,
    EmailPreferenceContainer,
    RecipientVerify,
    PoemPage,
    FloraVerse,
    PlayBook,
    CommunicationCenter,
    RemindersOptOutPage,
    EditRecipientContainer,
    CelebrationsRemindersPage,
};

const FontsWrapper = () => {
    // verify with Google Fonts that the output is exactly what's being added into the string
    const fonts = [
        'Quattrocento',
        'EB Garamond',
        'Roboto',
        'Dela Gothic One',
        'Dosis',
        'Raleway:wght@400..900',
        'Roboto Condensed',
        'Lato',
        'Playfair Display',
        'Roboto Mono',
    ];
    const processedFonts = fonts.map((font, i) => {
        if (i === 0) return 'family=' + font.replaceAll(' ', '+');
        return '&family=' + font.replaceAll(' ', '+');
    })
    return (
        <Helmet>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link href={`https://fonts.googleapis.com/css2?${processedFonts.join('')}&display=swap`} rel="stylesheet" />
        </Helmet>
    );
}

const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

const MyAccount = ({
    classes, module, isUsingMFE, page, isDadJokeGPT
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const parentElem = useRef(null);
    const prevLocationRef = useRef(location.pathname);

    const userProfile = useSelector((state) => state.account.userProfile || state.member.profile);
    const profileAndPassportInfo = useSelector(getProfileInfo);
    const featureFlags = useSelector(getFeatureFlags);
    const brandId = useSelector(getCurrentBrandId);
    const isAuthenticatedStatus = useSelector(getIsAuthenticatedStatus);


    if (!userProfile?.isAuthenticated && REACT_APP_INJECTABLE === 'false') {
        userProfile.isAuthenticated = (userProfile?.membershipType === 'R' || userProfile?.membershipType === 'P') && isAuthenticatedStatus
    }
    useEffect(() => {
        dispatch(EventDispatcher.fetchUserProfile());
    }, []);

    useEffect(() => {
        // handle redirect to replacement order on order details page
        if (location.pathname.includes('/orders') && location.pathname !== prevLocationRef.current) {
            history.go();
        }
        prevLocationRef.current = location.pathname;
    }, [location.pathname]);

    const renderLayout = () => {
        if (module === 'OrderDetails' || module === 'TrackOrder') {
            return (
                <OrderDetailsContextProvider>
                    <Layout page={{ account: true, type: 'ordertracking' }} userProfile={userProfile} featureFlags={featureFlags} isUsingMFE={isUsingMFE} brandId={brandId} isDadJokeGPT={isDadJokeGPT} />
                </OrderDetailsContextProvider>
            );
        }
        return (
            <Layout page={{ account: true, type: 'ordertracking' }} userProfile={userProfile} featureFlags={featureFlags} isUsingMFE={isUsingMFE} brandId={brandId} isDadJokeGPT={isDadJokeGPT} />
        );
    };

    // Any logic for redirecting to homepage can go here
    if (
        (module === 'RemindersOptOutPage' && !featureFlags['is-reminders-static-page-showing'])
        ||
        (module === 'PlayBook' && featureFlags['is-date-playbook-hidden'])
    ) {
        window?.location?.replace('https://www.1800flowers.com');
        return null;
    }

    let Layout = Components[module];
    const breakpointExpr = `(min-width: ${CUSTOM_BREAKPOINTS.tabLandscape}px)`;
    const isHidingWeatherbanner = featureFlags?.['pages-to-hide-weather-banner-on'][window?.location?.pathname] || false;
    const isHidingAccountLeftMenu = featureFlags?.['is-account-left-menu-hidden-on-route'][window?.location?.pathname] || false;

    const isTopMarginDisabled = !!featureFlags?.['is-account-top-margin-disabled']?.[window?.location?.pathname];

    if (isEmpty(userProfile) || !brandId) return null;

    return (
        <>
            {featureFlags['is-cs-delivery-alert-message-enabled'] && !userProfile?.isAuthenticated && (
                !isHidingWeatherbanner && <AlertMessage queryTitle="My Orders" />
            )}
            {featureFlags['is-cs-delivery-alert-message-enabled'] && userProfile?.isAuthenticated && (
                !isHidingWeatherbanner && <AlertMessage queryTitle="My Orders Signed In" />
            )}
            <GraphqlRelationship />
            <FontsWrapper />
            <Media query={breakpointExpr}>
                {(matches) => (
                    matches ? (
                        // Desktop site
                        <div ref={parentElem} id="desktop-main-container" className={`${classes.main} ${isAuthenticatedStatus || isTopMarginDisabled ? 'no-margin-top' : ''} no-margin-bottom desktop ${brandId}`}>
                            {isAuthenticatedStatus ? (
                                <Grid container>
                                    {!isHidingAccountLeftMenu && <AccountLeftMenu profileAndPassportInfo={profileAndPassportInfo} />}
                                    {renderLayout()}
                                </Grid>
                            ) : (
                                renderLayout()
                            )}
                        </div>
                    ) : (
                        // Mobile site
                        <div ref={parentElem} id="mobile-main-container" className={`${classes.main}`}>
                            {renderLayout()}
                        </div>
                    ))}
            </Media>

            <UIModal />
        </>
    );
};

MyAccount.defaultProps = {
    page: {},
};

MyAccount.propTypes = {
    classes: object.isRequired,
    module: string.isRequired,
    page: shape({
        account: bool,
    }),
};

export default withStyles(styles, { withTheme: true })(MyAccount);
